import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { CircularProgress } from '@mui/material';
import { fetchBusiness4FlyoTicketDetails } from '../services/api';
import B4FFlightDetailsTab from '../components/business4flyo/B4FFlightDetailsTab';
import B4FTicketInfoTab from '../components/business4flyo/B4FTicketInfoTab';
import B4FTravellerTab from '../components/business4flyo/B4FTravellerTab';
import B4FServicesTab from '../components/business4flyo/B4FServicesTab';
import B4FSourceDocumentsTab from '../components/business4flyo/B4FSourceDocumentsTab';
import B4FClientsAgentTab from '../components/business4flyo/B4FClientsAgentTab';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import '../pages/CheckInDetails.css';
import { BUSINESS4FLYO_ADMIN_API_BASE_URL } from '../config/api';

// Notification preview images mapping (S3 URLs)
const NOTIFICATION_PREVIEW_IMAGES = {
  'SMART_TICKET': 'https://hh-boarding-pass.s3.ap-south-1.amazonaws.com/file_uploads/622_1772701050284_smart_ticket.jpg',
  'CHECKIN_COMPLETED': 'https://hh-boarding-pass.s3.ap-south-1.amazonaws.com/file_uploads/622_1772701083039_checkin_completed.jpg',
  'CHECKIN_OPTIN': 'https://hh-boarding-pass.s3.ap-south-1.amazonaws.com/file_uploads/622_1772701761482_checkin_optin.png',
  'CHECKIN_FAILED': 'https://hh-boarding-pass.s3.ap-south-1.amazonaws.com/file_uploads/622_1772701742317_checkin_failed.png',
};

/**
 * Get status indicator color class
 * @param {string} status - Status value
 * @returns {string} Tailwind CSS class for status indicator
 */
const getStatusIndicatorColor = (status) => {
  const statusColors = {
    completed: 'bg-green-500',
    failed: 'bg-red-500',
    in_progress: 'bg-yellow-500',
    pending: 'bg-gray-400',
    none: 'bg-gray-400'
  };
  return statusColors[status?.toLowerCase()] || statusColors.none;
};

/**
 * Get status text color class
 * @param {string} status - Status value
 * @returns {string} Tailwind CSS class for status text
 */
const getStatusTextColor = (status) => {
  const textColors = {
    completed: 'text-green-700',
    failed: 'text-red-700',
    in_progress: 'text-yellow-700',
    pending: 'text-gray-600',
    none: 'text-gray-600'
  };
  return textColors[status?.toLowerCase()] || textColors.none;
};

/**
 * Notification Option Component
 */
const NotificationOption = React.memo(({
  status,
  title,
  description,
  onClick,
  previewImage,
  isLast = false
}) => {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div
      className={`flex items-center w-full px-4 py-3 text-sm hover:bg-gray-100 transition-colors ${
        isLast ? '' : 'border-b border-gray-100'
      }`}
    >
      <button
        className="flex-1 flex items-center text-left"
        onClick={onClick}
      >
        <div className={`w-2 h-2 rounded-full ${getStatusIndicatorColor(status)} mr-3 flex-shrink-0`}></div>
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${getStatusTextColor(status)}`}>
            {title}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {description}
          </div>
        </div>
      </button>
      {/* Info Icon with Hover Preview Tooltip */}
      <div
        className="relative ml-2 flex-shrink-0"
        onMouseEnter={(e) => {
          e.stopPropagation();
          setShowPreview(true);
        }}
        onMouseLeave={(e) => {
          e.stopPropagation();
          setShowPreview(false);
        }}
      >
        <button
          type="button"
          className="p-1 rounded-md hover:bg-gray-200 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <InfoCircledIcon
            className="w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors cursor-help"
          />
        </button>
        {/* Hover Preview Tooltip - Fixed Position */}
        {showPreview && (
          <div
            className="fixed z-[9999]"
            style={{
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
            onMouseEnter={() => setShowPreview(true)}
            onMouseLeave={() => setShowPreview(false)}
          >
            <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-3 w-80">
              <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <InfoCircledIcon className="w-3.5 h-3.5 text-blue-500" />
                {title} - Preview
              </div>
              <div className="rounded-lg border border-gray-100 overflow-hidden bg-gray-50">
                <img
                  src={previewImage}
                  alt={`${title} preview`}
                  className="w-full h-auto max-h-[70vh] object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden flex-col items-center justify-center h-48 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">Preview not available</span>
                </div>
              </div>
            </div>
            {/* Arrow pointing left */}
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2 -ml-2 w-0 h-0"
              style={{
                borderTop: '8px solid transparent',
                borderBottom: '8px solid transparent',
                borderRight: '8px solid white',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
});

NotificationOption.displayName = 'NotificationOption';

/**
 * Business4Flyo Check-in Details Page
 * @returns {JSX.Element} Business4FlyoCheckInDetailsPage component
 */
const Business4FlyoCheckInDetailsPage = () => {
  const { ticketId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [activeTab, setActiveTab] = useState('flightDetails');
  
  // Notification dropdown state
  const [showNotificationMenu, setShowNotificationMenu] = useState(false);
  const [showReasonDialog, setShowReasonDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState('success');
  const notificationMenuRef = useRef(null);
  
  // Get source tab from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const sourceTab = queryParams.get('source') || 'business4FlyoWebCheckIn';

  // Function to show toast message
  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  // Handle notification menu click
  const handleNotificationMenuClick = () => {
    setShowNotificationMenu(!showNotificationMenu);
  };

  // Handle notification menu close
  const handleNotificationMenuClose = () => {
    setShowNotificationMenu(false);
  };

  // Handle click outside to close notification dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationMenuRef.current &&
        !notificationMenuRef.current.contains(event.target)
      ) {
        setShowNotificationMenu(false);
      }
    };

    if (showNotificationMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotificationMenu]);

  // Function to send notification
  const sendNotification = async (notificationType, reasonText = '') => {
    try {
      setNotificationLoading(true);
      handleNotificationMenuClose();

      const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhZ2VudElkIjoyLCJhZ2VudFRlYW1NZW1iZXJJZCI6MSwidHlwZSI6ImFnZW50X3RlYW1fbWVtYmVyIiwiaWF0IjoxNzY3ODk5MjkyLCJleHAiOjE3Njg1MDQwOTJ9.PqfP15-xAl2T_TY319XY-r_e_28hFFuee-eRWHcc0i8';
      
      const response = await fetch(`${BUSINESS4FLYO_ADMIN_API_BASE_URL}/triggerNotification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ticketId: parseInt(ticketId),
          notificationType: notificationType,
          reason: reasonText
        })
      });

      const data = await response.json();

      if (response.ok) {
        showToastMessage(data.message || 'Notification sent successfully', 'success');
      } else {
        showToastMessage(data.message || 'Failed to send notification', 'error');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      showToastMessage('Error sending notification. Please try again.', 'error');
    } finally {
      setNotificationLoading(false);
    }
  };

  // Function to load ticket data (with loader - for initial load)
  const loadTicketData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBusiness4FlyoTicketDetails(ticketId);
      setTicketData(data);
    } catch (err) {
      setError(err.message || 'Failed to load ticket details');
      console.error('Error loading ticket data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to silently refresh data (no loader - for background updates)
  const silentRefresh = async () => {
    if (!ticketId) return;
    try {
      const data = await fetchBusiness4FlyoTicketDetails(ticketId);
      setTicketData(data);
    } catch (err) {
      console.error('Error refreshing ticket data:', err);
      // Silent fail - don't show error to user for background refresh
    }
  };

  // Fetch ticket details on mount and when ticketId changes
  useEffect(() => {
    if (ticketId) {
      loadTicketData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // Function to refresh data after updates
  const handleUpdate = () => {
    silentRefresh();
  };

  if (loading) {
    return (
      <MainLayout activeTab={sourceTab} setActiveTab={() => {}}>
        <div className="flex-1 flex items-center justify-center">
          <CircularProgress size={32} />
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout activeTab={sourceTab} setActiveTab={() => {}}>
        <div className="flex-1 p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-red-700 mb-1">Error</h3>
            <p className="text-xs text-red-600">{error}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!ticketData) {
    return (
      <MainLayout activeTab={sourceTab} setActiveTab={() => {}}>
        <div className="flex-1 p-4">
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-600">No data found</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const tabs = [
    { id: 'flightDetails', label: 'Flight Details' },
    { id: 'ticketInfo', label: 'Ticket Info' },
    { id: 'traveller', label: 'Traveller' },
    { id: 'services', label: 'Services' },
    { id: 'sourceDocuments', label: 'Source & Documents' },
    { id: 'clientsAgent', label: 'Clients & Agent' }
  ];
  const resolvedAgentId = ticketData.agent?.agentId || ticketData.agent?.id;

  return (
    <MainLayout activeTab={sourceTab} setActiveTab={() => {}}>
      <div className="flex-1 flex flex-col h-screen overflow-hidden p-3">
        {/* Toast Notification */}
        {showToast && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            toastType === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            <p className="text-sm font-medium">{toastMessage}</p>
          </div>
        )}

        {/* Reason Input Dialog */}
        {showReasonDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-xl font-bold mb-4">
                Enter Reason for Failed Check-in
              </h2>
              <textarea
                className="w-full p-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for failed check-in..."
              ></textarea>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowReasonDialog(false);
                    setReason('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    sendNotification('webCheckinFailed', reason);
                    setShowReasonDialog(false);
                    setReason('');
                  }}
                  disabled={!reason.trim() || notificationLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {notificationLoading ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold text-gray-900">Business4Flyo Check-in Details</h1>
              {ticketData.pnr && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                  PNR: {ticketData.pnr}
                </span>
              )}
            </div>
            
            {/* Send Notification Dropdown */}
            <div className="relative" ref={notificationMenuRef}>
              <button
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                onClick={handleNotificationMenuClick}
                disabled={notificationLoading}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
                Send Notification
                <span
                  className={`transition-transform duration-200 ${
                    showNotificationMenu ? 'rotate-180' : ''
                  }`}
                >
                  ▼
                </span>
              </button>
              {showNotificationMenu && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-medium text-gray-700 text-sm">
                        Select Notification Type
                      </h3>
                    </div>
                    <NotificationOption
                      status="completed"
                      title="Send Smart Ticket"
                      description="Send smart ticket confirmation"
                      onClick={() => sendNotification('ticketConfirmation')}
                      previewImage={NOTIFICATION_PREVIEW_IMAGES['SMART_TICKET']}
                    />
                    <NotificationOption
                      status="completed"
                      title="Check-in Completed"
                      description="Notify about successful check-in"
                      onClick={() => sendNotification('webCheckinCompleted')}
                      previewImage={NOTIFICATION_PREVIEW_IMAGES['CHECKIN_COMPLETED']}
                    />
                    <NotificationOption
                      status="in_progress"
                      title="Check-in Opt-in"
                      description="Send check-in opt-in notification"
                      onClick={() => sendNotification('webCheckinOptIn')}
                      previewImage={NOTIFICATION_PREVIEW_IMAGES['CHECKIN_OPTIN']}
                    />
                    <NotificationOption
                      status="failed"
                      title="Check-in Failed"
                      description="Send notification about failed check-in"
                      onClick={() => setShowReasonDialog(true)}
                      previewImage={NOTIFICATION_PREVIEW_IMAGES['CHECKIN_FAILED']}
                      isLast
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-2 flex-shrink-0">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab ${activeTab === tab.id ? 'tab-active' : ''}`}
                style={{
                  padding: '10px 14px',
                  borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : 'none',
                  color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  fontSize: '13px'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {activeTab === 'flightDetails' && (
              <B4FFlightDetailsTab 
                flights={ticketData.flights || []} 
                ticketData={ticketData}
                ticketId={ticketId}
                agentId={resolvedAgentId}
                onUpdate={handleUpdate}
              />
            )}
            {activeTab === 'ticketInfo' && (
              <B4FTicketInfoTab 
                ticketData={ticketData}
                amount={ticketData.amount}
                checkIn={ticketData.checkIn}
                serviceAmount={ticketData.serviceAmount || []}
                ticketId={ticketId}
                agentId={resolvedAgentId}
                onUpdate={handleUpdate}
              />
            )}
            {activeTab === 'traveller' && (
              <B4FTravellerTab 
                flights={ticketData.flights || []}
                ticketId={ticketId}
                clientId={ticketData.clients && ticketData.clients.length > 0 ? ticketData.clients[0].clientId : null}
                agentId={resolvedAgentId}
                onUpdate={handleUpdate}
              />
            )}
            {activeTab === 'services' && (
              <B4FServicesTab 
                flights={ticketData.flights || []}
                ticketId={ticketId}
                clientId={ticketData.clients && ticketData.clients.length > 0 ? ticketData.clients[0].clientId : null}
                agentId={resolvedAgentId}
                onUpdate={handleUpdate}
              />
            )}
            {activeTab === 'sourceDocuments' && (
              <B4FSourceDocumentsTab 
                source={ticketData.source || []}
                attachments={ticketData.attachments || []}
                customerDocuments={ticketData.customerDocuments || []}
                ticketId={ticketId}
                agentId={resolvedAgentId}
                onUpdate={handleUpdate}
              />
            )}
            {activeTab === 'clientsAgent' && (
              <B4FClientsAgentTab 
                clients={ticketData.clients || []}
                agent={ticketData.agent}
                agentId={resolvedAgentId}
                onUpdate={handleUpdate}
              />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Business4FlyoCheckInDetailsPage;
